import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { AdvertisingAd } from '@borradh-workspace/api-client/types';
import { Copy, ExternalLink, Image, Video } from 'lucide-react';
import { useState } from 'react';
import {
  AD_METRICS,
  METRIC_PRESETS,
  describeMetric,
  divide,
  formatMetric,
  formatMoney,
} from './advertising-metrics';
import {
  DeliveryBadge,
  Hint,
  currencyName,
  formatTimestamp,
} from './advertising-ui';

export function AdvertisingDetail({
  ad,
  onClose,
}: { ad: AdvertisingAd | null; onClose: () => void }) {
  const [copyStatus, setCopyStatus] = useState('Copy ad ID');
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const copy = async () => {
    if (!ad) return;
    try {
      await navigator.clipboard.writeText(ad.metaAdId);
      setCopyStatus('Copied');
    } catch {
      setCopyStatus('Copy unavailable');
    }
  };
  return (
    <Sheet
      open={ad !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setCopyStatus('Copy ad ID');
        }
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        {ad && (
          <>
            <SheetHeader className="border-b p-6">
              <div className="mb-2 flex items-center gap-2">
                <DeliveryBadge status={ad.status} />
                <Hint label={currencyName(ad.currency)}>
                  <Badge variant="secondary">
                    {ad.currency ?? 'Unknown currency'}
                  </Badge>
                </Hint>
              </div>
              <SheetTitle className="pr-4 text-xl">{ad.name}</SheetTitle>
              <SheetDescription>
                {ad.organization} · {ad.platform} · {ad.format}
              </SheetDescription>
            </SheetHeader>
            <Tabs defaultValue="overview" className="gap-5 px-6 pb-6">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="metrics">All metrics</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-5">
                <div className="grid grid-cols-2 gap-3 rounded-lg border p-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Amount spent · {ad.currency ?? 'unknown'}
                    </p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">
                      {formatMoney(ad.spend, ad.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Converted to EUR
                    </p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">
                      {formatMoney(ad.spendEur)}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <Hint label="Conversations with a customer reply after the first sent business message, divided by all ad-attributed conversations created in this period.">
                      <button type="button" className="cursor-help text-left">
                        Conversations with replies
                      </button>
                    </Hint>
                    <span className="font-medium tabular-nums">
                      {ad.replied} / {ad.conversations}
                    </span>
                  </div>
                  <Progress
                    aria-label="Customer reply rate"
                    value={divide(ad.replied, ad.conversations, 100) ?? 0}
                    className="h-1.5"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {ad.conversations
                      ? `${((ad.replied / ad.conversations) * 100).toFixed(1)}% replied`
                      : 'No attributed conversations in this period'}
                  </p>
                </div>
                {ad.syncError && (
                  <div
                    role="alert"
                    className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive"
                  >
                    {ad.syncError}
                  </div>
                )}
                <section className="overflow-hidden rounded-lg border">
                  <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3 text-sm font-medium">
                    {ad.format === 'Video' ? (
                      <Video className="size-4" />
                    ) : (
                      <Image className="size-4" />
                    )}
                    Ad creative
                  </div>
                  {ad.thumbnailUrl && failedImage !== ad.thumbnailUrl && (
                    <img
                      src={ad.thumbnailUrl}
                      alt={ad.name}
                      onError={() => setFailedImage(ad.thumbnailUrl)}
                      className="mx-auto max-h-64 w-full bg-muted object-contain"
                    />
                  )}
                  <div className="space-y-2 p-4">
                    {ad.headline && (
                      <p className="font-medium">{ad.headline}</p>
                    )}
                    {ad.primaryText ? (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                        {ad.primaryText}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No saved creative copy.
                      </p>
                    )}
                  </div>
                </section>
                <dl className="space-y-3 text-sm">
                  {[
                    ['Campaign', ad.campaignId ?? 'Not linked'],
                    ['Ad account', ad.accountId ?? 'Not recorded'],
                    ['Meta ad ID', ad.metaAdId],
                    ['Latest data sync', formatTimestamp(ad.lastSyncAt)],
                    ['Days with delivery data', String(ad.daysWithData)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-start justify-between gap-4"
                    >
                      <dt className="shrink-0 text-muted-foreground">
                        {label}
                      </dt>
                      <dd className="break-all text-right">{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void copy()}
                  >
                    <Copy className="size-4" />
                    {copyStatus}
                  </Button>
                  {ad.permalink &&
                    /^https:\/\/(www\.)?facebook\.com\//.test(ad.permalink) && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={ad.permalink} target="_blank" rel="noreferrer">
                          <ExternalLink className="size-4" />
                          View on Facebook
                        </a>
                      </Button>
                    )}
                </div>
              </TabsContent>
              <TabsContent value="metrics" className="space-y-6">
                {['conversations', 'delivery', 'engagement'].map((group) => {
                  const ids = METRIC_PRESETS[group].metrics;
                  const metrics = AD_METRICS.filter(
                    (metric) =>
                      ids.includes(metric.id) &&
                      !(group !== 'conversations' && metric.id === 'spend')
                  ).map((metric) => describeMetric(metric, ad.conversionKind));
                  return (
                    <section key={group}>
                      <h3 className="mb-2 text-sm font-semibold">
                        {METRIC_PRESETS[group].label}
                      </h3>
                      <dl className="divide-y divide-border/60">
                        {metrics.map((metric) => (
                          <div
                            key={metric.id}
                            className="flex items-center justify-between gap-4 py-2.5 text-sm"
                          >
                            <dt className="text-muted-foreground">
                              <Hint
                                label={`${metric.name}. ${metric.description} Source: ${metric.source}.`}
                              >
                                <button
                                  type="button"
                                  className="cursor-help text-left underline decoration-dotted underline-offset-4"
                                >
                                  {metric.label}
                                </button>
                              </Hint>
                            </dt>
                            <dd className="font-medium tabular-nums">
                              {formatMetric(
                                metric,
                                metric.value(ad),
                                ad.currency
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  );
                })}
                <p className="text-xs leading-relaxed text-muted-foreground">
                  A dash means the value is unavailable. Unique reach and
                  frequency across several days cannot be added from daily
                  snapshots.
                </p>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
